/**
 * HowItWorks — the anchor visual explainer for the Nodera platform.
 *
 * Route: /how-it-works
 *
 * Structure (feels like a slide deck but scrolls like a landing page):
 *   1. Hero — one-sentence "what is Nodera"
 *   2. The 3-stage workflow diagram (the star)
 *   3. Zoom into each stage (Activate / Launch / Track)
 *   4. Who benefits — clinic / vendor / consumer split
 *   5. Before/After compression table (linked to whitepaper for depth)
 *   6. Programs that ship on it
 *   7. Honest positioning
 *   8. CTA
 */
import { Link } from "react-router-dom";
import { Section } from "@/components/Section";
import { usePageMeta } from "@/lib/usePageMeta";
import {
  ArrowRight, BarChart3, Boxes, Building2, CheckCircle2, ClipboardList, Clock, Download,
  FileText, Handshake, Layers, Rocket, ShieldCheck, Sparkles, User, Users, Workflow,
} from "lucide-react";

const STAGES = [
  {
    n: "1",
    tag: "Activate",
    icon: Boxes,
    color: "blue",
    headline: "Clinic picks a program.",
    detail: "Pick from a catalog of proven cash-pay programs. Nodera hands you the vendor stack, workflow, SOPs, marketing collateral, and pricing model — all pre-built.",
    steps: ["Choose program", "Review vendors", "Click Activate"],
  },
  {
    n: "2",
    tag: "Launch",
    icon: Rocket,
    color: "violet",
    headline: "Clinic launches for a consumer.",
    detail: "A 1-2-3 wizard triggers automatic fan-out. Every vendor tied to the program is notified with the consumer's contact info + a service due-by date. Vendors engage the consumer directly.",
    steps: ["Select program + date", "Enter consumer info", "Click Launch"],
  },
  {
    n: "3",
    tag: "Track",
    icon: BarChart3,
    color: "emerald",
    headline: "Clinic tracks it all in one place.",
    detail: "Dashboard rolls up launches, dispatch success, at-risk vendors, and revenue. Nodera flags problems before they hit the consumer.",
    steps: ["Recent launches", "Vendor health", "Revenue pulse"],
  },
];

const PROGRAMS = [
  { name: "Sleep & Recovery", desc: "Home sleep tests, PAP therapy, coaching", price: "$1,800–$3,200" },
  { name: "Metabolic / Weight", desc: "Labs, GLP-1 protocols, coaching", price: "$2,400–$8,400/yr" },
  { name: "Longevity", desc: "Full labs, biological age, wearables", price: "$3,500–$9,500" },
  { name: "Hormone / HRT", desc: "Screening, labs, prescribing, follow-up", price: "$1,800–$4,200/yr" },
  { name: "Recovery Therapies", desc: "Infusions, injections, monitoring", price: "$650–$2,400" },
];

const COMPRESSION = [
  { line: "Program design", inhouse: "60–120 hrs", nodera: "4–8 hrs" },
  { line: "Vendor sourcing", inhouse: "40–80 hrs", nodera: "0 (pre-vetted)" },
  { line: "Workflow design", inhouse: "40–80 hrs", nodera: "0 (inherited)" },
  { line: "Vendor management", inhouse: "100–200 hrs/yr", nodera: "20–40 hrs/yr" },
  { line: "Year 1, single program", inhouse: "$85K–$171K", nodera: "$15K–$30K equiv." },
];

// -------------------------------------------------------------------
// Colored dot for the top-level stage badges. Tailwind config already
// includes the Nodera palette; using explicit classes keeps JIT happy.
// -------------------------------------------------------------------
const COLOR_CLASSES = {
  blue: {
    ring: "ring-blue-500/30 bg-blue-50",
    accent: "text-blue-700",
    dot: "bg-blue-500",
    tint: "bg-blue-500/5",
    border: "border-blue-200",
  },
  violet: {
    ring: "ring-violet-500/30 bg-violet-50",
    accent: "text-violet-700",
    dot: "bg-violet-500",
    tint: "bg-violet-500/5",
    border: "border-violet-200",
  },
  emerald: {
    ring: "ring-emerald-500/30 bg-emerald-50",
    accent: "text-emerald-700",
    dot: "bg-emerald-500",
    tint: "bg-emerald-500/5",
    border: "border-emerald-200",
  },
};

function StageNode({ stage }) {
  const c = COLOR_CLASSES[stage.color];
  const Icon = stage.icon;
  return (
    <div
      data-testid={`stage-${stage.n}`}
      className={`relative flex-1 flex flex-col rounded-2xl border ${c.border} bg-white shadow-sm hover:shadow-md transition-shadow`}
    >
      <div className={`px-5 pt-5 pb-4 border-b border-slate-100`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl ring-4 ${c.ring} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${c.accent}`} />
            </div>
            <div>
              <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${c.accent}`}>Stage {stage.n}</div>
              <div className="font-display text-xl font-bold text-slate-900 tracking-tight">{stage.tag}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="px-5 py-4 flex-1 flex flex-col">
        <p className="text-sm text-slate-700 leading-relaxed">
          <strong className="text-slate-900">{stage.headline}</strong> {stage.detail}
        </p>
        <div className={`mt-auto rounded-lg ${c.tint} p-3`}>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Steps</div>
          <ul className="text-xs text-slate-800 space-y-1">
            {stage.steps.map((s, i) => (
              <li key={i} className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                {s}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// ExplainerLoop — a 10-second auto-playing animation that walks
// viewers through Activate → Launch → Track BEFORE they read the
// static workflow diagram below. Pure CSS/SVG, no video file.
//
// Loop timing (10s, seamless):
//   0.00 – 3.33s  Activate (blue)  — clinic picks a program
//   3.33 – 6.66s  Launch (violet)  — vendors fan out
//   6.66 – 10.0s  Track (emerald)  — bars rise on the dashboard
//
// The animation is *decorative*; the actual product story sits in
// the static diagram directly below it. Marked aria-hidden and
// respects prefers-reduced-motion.
// -------------------------------------------------------------------
function ExplainerLoop() {
  return (
    <div
      data-testid="explainer-loop"
      className="rounded-3xl overflow-hidden border border-slate-900/10 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 text-white shadow-lg"
    >
      <style>{`
        @keyframes nh-stage {
          0%, 33% { opacity: 1; transform: translateY(0); }
          34%, 100% { opacity: 0; transform: translateY(-4px); }
        }
        @keyframes nh-node1 {
          0%, 30% { opacity: 1; }
          32%, 100% { opacity: 0.25; }
        }
        @keyframes nh-node2 {
          0%, 30% { opacity: 0.25; }
          33%, 63% { opacity: 1; }
          65%, 100% { opacity: 0.25; }
        }
        @keyframes nh-node3 {
          0%, 63% { opacity: 0.25; }
          66%, 100% { opacity: 1; }
        }
        @keyframes nh-packet {
          0% { offset-distance: 0%; opacity: 0; }
          5% { opacity: 1; }
          95% { opacity: 1; }
          100% { offset-distance: 100%; opacity: 0; }
        }
        @keyframes nh-fanout {
          0%, 33% { transform: scale(0); opacity: 0; }
          40% { transform: scale(1); opacity: 1; }
          63% { transform: scale(1.6); opacity: 0; }
          64%, 100% { transform: scale(0); opacity: 0; }
        }
        @keyframes nh-bar {
          0%, 66% { transform: scaleY(0.1); }
          85%, 100% { transform: scaleY(1); }
        }
        @keyframes nh-progress {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(0%); }
        }
        @keyframes nh-dot1 { 0%,30%{background:#60a5fa;transform:scale(1.15);} 33%,100%{background:rgba(255,255,255,0.25);transform:scale(1);} }
        @keyframes nh-dot2 { 0%,30%{background:rgba(255,255,255,0.25);transform:scale(1);} 33%,63%{background:#a78bfa;transform:scale(1.15);} 65%,100%{background:rgba(255,255,255,0.25);transform:scale(1);} }
        @keyframes nh-dot3 { 0%,63%{background:rgba(255,255,255,0.25);transform:scale(1);} 66%,100%{background:#34d399;transform:scale(1.15);} }

        .nh-loop .nh-stage-label { animation: nh-stage 10s infinite; }
        .nh-loop .nh-stage-label--1 { animation-delay: 0s; }
        .nh-loop .nh-stage-label--2 { animation-delay: -6.66s; }
        .nh-loop .nh-stage-label--3 { animation-delay: -3.33s; }
        .nh-loop .nh-node--1 { animation: nh-node1 10s infinite; }
        .nh-loop .nh-node--2 { animation: nh-node2 10s infinite; }
        .nh-loop .nh-node--3 { animation: nh-node3 10s infinite; }
        .nh-loop .nh-packet { offset-path: path('M 60 90 C 160 90, 200 90, 260 90 S 400 90, 460 90'); animation: nh-packet 10s infinite linear; }
        .nh-loop .nh-fan { transform-origin: 260px 90px; transform-box: fill-box; animation: nh-fanout 10s infinite ease-out; }
        .nh-loop .nh-bar { transform-origin: bottom; animation: nh-bar 10s infinite cubic-bezier(.2,.7,.2,1); }
        .nh-loop .nh-progress { animation: nh-progress 10s infinite linear; }
        .nh-loop .nh-tick--1 { animation: nh-dot1 10s infinite; }
        .nh-loop .nh-tick--2 { animation: nh-dot2 10s infinite; }
        .nh-loop .nh-tick--3 { animation: nh-dot3 10s infinite; }

        @media (prefers-reduced-motion: reduce) {
          .nh-loop *, .nh-loop *::before, .nh-loop *::after { animation: none !important; }
          .nh-loop .nh-node--1, .nh-loop .nh-node--2, .nh-loop .nh-node--3 { opacity: 1 !important; }
        }
      `}</style>

      <div className="nh-loop grid md:grid-cols-[1fr_1.4fr] gap-0" aria-hidden="true">
        {/* Left: rotating label */}
        <div className="p-6 sm:p-7 border-b md:border-b-0 md:border-r border-white/10 flex flex-col justify-between min-h-[220px]">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-semibold text-blue-200 mb-3">
              <Sparkles className="w-3 h-3" /> 10-second walkthrough
            </div>
            <div className="relative h-[92px]">
              <div className="nh-stage-label nh-stage-label--1 absolute inset-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-300">Stage 1 · Activate</div>
                <div className="font-display text-2xl font-extrabold mt-1 leading-tight">Clinic picks a program.</div>
                <div className="text-sm text-blue-100/80 mt-1.5">From the catalog · vendor stack included.</div>
              </div>
              <div className="nh-stage-label nh-stage-label--2 absolute inset-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-300">Stage 2 · Launch</div>
                <div className="font-display text-2xl font-extrabold mt-1 leading-tight">Vendors fan out.</div>
                <div className="text-sm text-violet-100/80 mt-1.5">One click · every vendor notified.</div>
              </div>
              <div className="nh-stage-label nh-stage-label--3 absolute inset-0">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300">Stage 3 · Track</div>
                <div className="font-display text-2xl font-extrabold mt-1 leading-tight">Dashboard lights up.</div>
                <div className="text-sm text-emerald-100/80 mt-1.5">Launches · vendor health · revenue.</div>
              </div>
            </div>
          </div>

          {/* Stage ticks */}
          <div className="mt-5 flex items-center gap-2">
            <span className="nh-tick--1 w-2 h-2 rounded-full bg-white/25" />
            <span className="nh-tick--2 w-2 h-2 rounded-full bg-white/25" />
            <span className="nh-tick--3 w-2 h-2 rounded-full bg-white/25" />
            <span className="ml-auto text-[10px] uppercase tracking-[0.18em] text-white/50 font-semibold">Loop</span>
          </div>
        </div>

        {/* Right: SVG scene */}
        <div className="relative p-4 sm:p-6 flex items-center justify-center">
          <svg viewBox="0 0 520 180" className="w-full max-w-[520px] h-auto" role="img" aria-label="Activate, Launch, Track animation">
            {/* Connecting rail */}
            <path d="M 60 90 C 160 90, 200 90, 260 90 S 400 90, 460 90" stroke="rgba(255,255,255,0.15)" strokeWidth="2" fill="none" strokeDasharray="3 4" />

            {/* Node 1 — Activate (blue) */}
            <g className="nh-node--1">
              <circle cx="60" cy="90" r="26" fill="#1e3a8a" stroke="#60a5fa" strokeWidth="2" />
              <circle cx="60" cy="90" r="34" fill="none" stroke="#60a5fa" strokeOpacity="0.35" strokeWidth="1" />
              <rect x="49" y="79" width="22" height="22" rx="4" fill="none" stroke="#93c5fd" strokeWidth="1.6" />
              <path d="M53 87 h14 M53 91 h10 M53 95 h12" stroke="#93c5fd" strokeWidth="1.4" strokeLinecap="round" />
            </g>

            {/* Node 2 — Launch (violet) with fan-out dots */}
            <g className="nh-node--2">
              <circle cx="260" cy="90" r="26" fill="#4c1d95" stroke="#a78bfa" strokeWidth="2" />
              <circle cx="260" cy="90" r="34" fill="none" stroke="#a78bfa" strokeOpacity="0.35" strokeWidth="1" />
              {/* Rocket-ish glyph */}
              <path d="M260 78 L268 90 L260 102 L252 90 Z" fill="#c4b5fd" />
            </g>
            {/* fan-out dots (six directions) */}
            {[
              { dx: 60, dy: 0 }, { dx: -60, dy: 0 },
              { dx: 42, dy: 42 }, { dx: -42, dy: 42 },
              { dx: 42, dy: -42 }, { dx: -42, dy: -42 },
            ].map((d, i) => (
              <circle
                key={i}
                className="nh-fan"
                cx={260 + d.dx}
                cy={90 + d.dy}
                r="3.2"
                fill="#a78bfa"
                style={{ animationDelay: `${0.06 * i}s` }}
              />
            ))}

            {/* Node 3 — Track (emerald) with bars */}
            <g className="nh-node--3">
              <circle cx="460" cy="90" r="26" fill="#064e3b" stroke="#34d399" strokeWidth="2" />
              <circle cx="460" cy="90" r="34" fill="none" stroke="#34d399" strokeOpacity="0.35" strokeWidth="1" />
              {/* rising bars */}
              <g transform="translate(449,101)">
                <rect className="nh-bar" x="0"  y="-4"  width="4" height="4"  fill="#6ee7b7" style={{ transformOrigin: 'bottom', animationDelay: '0.0s' }} />
                <rect className="nh-bar" x="6"  y="-8"  width="4" height="8"  fill="#6ee7b7" style={{ transformOrigin: 'bottom', animationDelay: '0.1s' }} />
                <rect className="nh-bar" x="12" y="-12" width="4" height="12" fill="#6ee7b7" style={{ transformOrigin: 'bottom', animationDelay: '0.2s' }} />
                <rect className="nh-bar" x="18" y="-16" width="4" height="16" fill="#6ee7b7" style={{ transformOrigin: 'bottom', animationDelay: '0.3s' }} />
              </g>
            </g>

            {/* Traveling packet along the rail */}
            <circle className="nh-packet" cx="0" cy="0" r="4.5" fill="#fef3c7" />

            {/* Stage labels under each node */}
            <text x="60"  y="140" textAnchor="middle" fill="#cbd5e1" fontSize="11" fontWeight="600" fontFamily="ui-sans-serif, system-ui">Activate</text>
            <text x="260" y="140" textAnchor="middle" fill="#cbd5e1" fontSize="11" fontWeight="600" fontFamily="ui-sans-serif, system-ui">Launch</text>
            <text x="460" y="140" textAnchor="middle" fill="#cbd5e1" fontSize="11" fontWeight="600" fontFamily="ui-sans-serif, system-ui">Track</text>
          </svg>
        </div>
      </div>

      {/* Progress rail */}
      <div className="h-0.5 bg-white/5 overflow-hidden">
        <div className="nh-progress h-full w-full bg-gradient-to-r from-blue-400 via-violet-400 to-emerald-400" />
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// SVG arrow connector between stage cards (desktop only).
// On mobile the cards stack vertically and we hide the arrows.
// -------------------------------------------------------------------
function StageArrow() {
  return (
    <div className="hidden lg:flex flex-shrink-0 items-center px-2" aria-hidden="true">
      <svg width="42" height="24" viewBox="0 0 42 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M0 12 H30 M22 4 L34 12 L22 20"
          stroke="#cbd5e1"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </div>
  );
}

// -------------------------------------------------------------------
// The "engine band" that runs under the three stages — a horizontal
// gradient bar signaling "the Nodera platform runs continuously
// underneath every stage".
// -------------------------------------------------------------------
function EngineBand() {
  return (
    <div className="mt-6 rounded-2xl overflow-hidden border border-slate-900/10 bg-gradient-to-r from-slate-900 via-blue-900 to-violet-900 text-white">
      <div className="px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
            <Workflow className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-blue-200 font-semibold">The engine</div>
            <div className="font-display font-bold text-base leading-tight">Workflow orchestration + vendor coordination · running continuously.</div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-[11px] text-blue-100">
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Vendors notified</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> SLAs tracked</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Drift flagged</span>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// "Who benefits" panels — clinic, vendor, consumer.
// -------------------------------------------------------------------
const AUDIENCES = [
  {
    label: "Clinic",
    icon: Building2,
    color: "blue",
    points: [
      "Skip 12 months of program design + vendor sourcing",
      "Launch for a consumer in under 90 seconds",
      "Dashboard flags at-risk vendors before consumers feel it",
      "Same brand, same clinical protocol — no white-labeling awkwardness",
    ],
  },
  {
    label: "Vendor",
    icon: Handshake,
    color: "violet",
    points: [
      "One integration point instead of dozens of clinic contracts",
      "Standardized service requests with consumer contact + due-by dates",
      "Automatic notification means no missed launches",
      "Portal shows every dispatch across every clinic",
    ],
  },
  {
    label: "Consumer",
    icon: User,
    color: "emerald",
    points: [
      "Their clinic launches a program in one action",
      "Vendors reach out directly with clear next steps",
      "Every touchpoint is coordinated — no dropped handoffs",
      "The clinic stays the trusted voice throughout",
    ],
  },
];

function AudiencePanel({ audience }) {
  const c = COLOR_CLASSES[audience.color];
  const Icon = audience.icon;
  return (
    <div
      data-testid={`audience-${audience.label.toLowerCase()}`}
      className={`rounded-2xl border ${c.border} bg-white p-5`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ring-4 ${c.ring} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${c.accent}`} />
        </div>
        <div>
          <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${c.accent}`}>For the</div>
          <div className="font-display text-lg font-bold text-slate-900">{audience.label}</div>
        </div>
      </div>
      <ul className="space-y-2 text-sm text-slate-700 leading-relaxed">
        {audience.points.map((p, i) => (
          <li key={i} className="flex items-start gap-2">
            <CheckCircle2 className={`w-4 h-4 ${c.accent} flex-shrink-0 mt-0.5`} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// -------------------------------------------------------------------
// Main page.
// -------------------------------------------------------------------
export default function HowItWorks() {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  usePageMeta({
    title: "How Nodera Health works · Nodera",
    description:
      "A highly visual walkthrough of the Nodera platform. Activate → Launch → Track. See how clinics run consumer-centric cash-pay programs without the operating overhead.",
    image: `${origin}/og/how-nodera-works.png`,
    url: `${origin}/how-it-works`,
    type: "article",
  });

  return (
    <div data-testid="how-it-works-page">
      {/* Hero */}
      <Section className="pt-28 pb-10">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-blue-600 mb-4">
            <FileText className="w-3 h-3" /> How it works
            <span className="text-slate-300">•</span>
            <span className="text-slate-500 normal-case tracking-normal font-medium inline-flex items-center gap-1">
              <Clock className="w-3 h-3" /> 4 min read
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
            Nodera Health in one picture.
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">
            Clinics activate cash-pay programs from a catalog. They launch them for real consumers in three clicks. Nodera fans the work out to pre-vetted vendors — so the clinic never has to source, coordinate, or babysit a vendor stack again.
          </p>
        </div>
      </Section>

      {/* 10-second animated explainer — sits ABOVE the static diagram */}
      <Section className="pt-0 pb-6">
        <ExplainerLoop />
      </Section>

      {/* The workflow diagram — THE star */}
      <Section className="pt-0 pb-16">
        <div className="rounded-3xl bg-slate-50 border border-slate-200 p-6 sm:p-8" data-testid="workflow-diagram">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">The workflow</div>
              <h2 className="font-display text-2xl font-bold text-slate-900">Three stages · one platform.</h2>
            </div>
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" /> Automation runs continuously underneath
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-4 lg:gap-2 lg:items-stretch">
            <StageNode stage={STAGES[0]} />
            <StageArrow />
            <StageNode stage={STAGES[1]} />
            <StageArrow />
            <StageNode stage={STAGES[2]} />
          </div>

          <EngineBand />
        </div>
      </Section>

      {/* Zoom into each stage */}
      <Section className="pt-0 pb-16">
        <div className="max-w-4xl">
          <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight">What the clinic actually does.</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">Each stage is deliberately small. The platform absorbs the rest.</p>
        </div>

        <div className="mt-8 space-y-6">
          {STAGES.map((stage, i) => {
            const c = COLOR_CLASSES[stage.color];
            const Icon = stage.icon;
            return (
              <div
                key={stage.n}
                data-testid={`stage-zoom-${stage.n}`}
                className={`rounded-2xl border ${c.border} bg-white overflow-hidden`}
              >
                <div className="grid md:grid-cols-[280px_1fr]">
                  <div className={`${c.tint} p-6 flex flex-col justify-center`}>
                    <div className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold ${c.accent}`}>
                      Stage {stage.n} of {STAGES.length}
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-2xl ring-4 ${c.ring} flex items-center justify-center`}>
                        <Icon className={`w-5 h-5 ${c.accent}`} />
                      </div>
                      <div className="font-display text-3xl font-extrabold text-slate-900 tracking-tight">{stage.tag}</div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="font-display text-xl font-bold text-slate-900 tracking-tight">{stage.headline}</div>
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed">{stage.detail}</p>

                    <div className="mt-4 grid grid-cols-3 gap-2">
                      {stage.steps.map((s, si) => (
                        <div
                          key={si}
                          className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-xs"
                        >
                          <div className={`text-[10px] uppercase tracking-wider font-bold ${c.accent}`}>Step {si + 1}</div>
                          <div className="mt-0.5 text-slate-800 font-semibold">{s}</div>
                        </div>
                      ))}
                    </div>

                    {i === 1 && (
                      <div className="mt-4 rounded-lg bg-slate-900 text-white px-4 py-3 text-xs">
                        <div className="text-[10px] uppercase tracking-[0.18em] text-blue-200 font-semibold mb-1">Under the hood</div>
                        <div className="leading-relaxed">
                          On Launch → Nodera notifies every vendor with consumer contact info + service due-by date, creates an enrollment row for analytics, and starts tracking dispatch success in real time.
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Who benefits */}
      <Section className="pt-0 pb-16">
        <div className="max-w-4xl mb-8">
          <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Who benefits — and why.</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Every real cash-pay flow involves three parties. Nodera improves the experience for all three.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {AUDIENCES.map((a) => <AudiencePanel key={a.label} audience={a} />)}
        </div>
      </Section>

      {/* Compression table */}
      <Section className="pt-0 pb-16">
        <div className="max-w-4xl">
          <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Before Nodera vs. with Nodera.</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            The full accounting is in the{" "}
            <Link to="/resources/cost-of-cash-pay-programs" className="text-blue-700 hover:underline font-semibold">
              Real Cost white paper
            </Link>.
            The short version:
          </p>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="compression-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                <tr>
                  <th className="px-5 py-2.5 text-left">Line</th>
                  <th className="px-5 py-2.5 text-left">In-house build</th>
                  <th className="px-5 py-2.5 text-left">With Nodera</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {COMPRESSION.map((row) => (
                  <tr key={row.line} className="hover:bg-slate-50/60">
                    <td className="px-5 py-3 text-slate-700 font-medium">{row.line}</td>
                    <td className="px-5 py-3 font-mono text-[13px] text-slate-600">{row.inhouse}</td>
                    <td className="px-5 py-3 font-mono text-[13px] text-blue-700 font-semibold">{row.nodera}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* Programs that ship */}
      <Section className="pt-0 pb-16">
        <div className="max-w-4xl mb-6">
          <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Programs that ship out of the box.</h2>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Each with its own template — vendor stack, workflow, SOPs, marketing collateral, and pricing model.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="programs-grid">
          {PROGRAMS.map((p) => (
            <div key={p.name} className="rounded-2xl border border-slate-200 bg-white p-5 hover:border-blue-300 transition-colors">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-4 h-4 text-blue-600" />
                <div className="font-display font-bold text-slate-900">{p.name}</div>
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">{p.desc}</p>
              <div className="mt-3 text-[11px] font-mono text-slate-500">Typical price · {p.price}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Honest positioning */}
      <Section className="pt-0 pb-16">
        <div className="max-w-3xl rounded-3xl bg-slate-900 text-white p-8 sm:p-10">
          <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold text-blue-300 mb-3">
            <ShieldCheck className="w-3 h-3" /> The honest positioning
          </div>
          <h2 className="font-display text-3xl font-bold leading-tight tracking-tight">
            Nodera doesn&apos;t replace your clinicians, your license, your patients, or your brand.
          </h2>
          <p className="mt-4 text-slate-300 leading-relaxed text-[15px]">
            It replaces the operating layer that sits between those things and the outside world — vendor sourcing,
            vendor coordination, workflow orchestration, program design, and the ongoing management of all of the above.
          </p>
          <p className="mt-3 text-slate-300 leading-relaxed text-[15px]">
            If you already run cash-pay programs, we compress the operating cost. If you want to start, we let you skip
            the 12-month setup and launch this quarter.
          </p>
        </div>
      </Section>

      {/* CTA */}
      <Section className="pt-0 pb-24">
        <div className="max-w-3xl">
          <div className="rounded-2xl bg-white border border-slate-200 p-8 sm:p-10 flex items-center justify-between gap-6 flex-wrap">
            <div>
              <div className="font-display text-2xl font-bold text-slate-900 tracking-tight">See it against your clinic.</div>
              <p className="mt-2 text-slate-600 text-sm max-w-lg">
                A 30-minute walkthrough covers everything on this page — with your patient panel size, your state, and your program mix.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Link
                to="/demo"
                data-testid="how-it-works-cta-demo"
                className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5 py-2.5 text-sm font-semibold"
              >
                Book a walkthrough <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <a
                href="/whitepapers/how-nodera-works.pdf"
                data-testid="how-it-works-cta-pdf"
                download
                className="inline-flex items-center gap-1.5 border border-slate-300 text-slate-800 hover:bg-slate-50 rounded-full px-5 py-2.5 text-sm font-semibold"
              >
                <Download className="w-3.5 h-3.5" /> Download PDF
              </a>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
